import { Injectable } from '@angular/core';
import { GerinusService } from '../lib/gerinus.service';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import jsPDF from 'jspdf';
import { environment } from 'src/environments/environment';
import * as QRCode from 'qrcode';
import { interRegular } from '../../../assets/fonts/inter/Inter-Regular-normal';
import { interSemiBold } from '../../../assets/fonts/inter/Inter-SemiBold-normal';
import autoTable from 'jspdf-autotable';
import { DatePipe } from '@angular/common';
import { UploadService } from './upload.service';
import { IAddDispachInternalProcessSchema, ICorrectionDataInternalProcessSchema, IInternalProcessUserFeedbackSchema, INewInternalProcessSchema, ISendInternalProcessConsiderationsSchema, ISubmitExtendInternalProcessSchema, ITransmitInternalProcessSchema } from 'src/app/interfaces/internalProcess.interfaces';
import { internalProcessGeneratesDocumentsName, internalProcessStatusEnum, internalProcessStatusText } from 'src/app/Enums/internalProcessEnum';
import { ISendFileProcessPdf } from 'src/app/interfaces/processCommon.interface';
import { processGeneratesDocumentsName } from 'src/app/Enums/commonProcessEnum';
import * as dayjs from 'dayjs';

@Injectable({
    providedIn: 'root',
})
export class InternalProcessService extends GerinusService {

    private filterSearch = new BehaviorSubject<any>(null);

    constructor(
        public http: HttpClient,
        private datePipe: DatePipe,
        private uploadService: UploadService,
    ) {
        super(http);
        this.entityName = 'processo_interno';
        this.idField = 'PI_CodigoProcessoInterno';
        this.model = 'ProcessoInternoModel';
    }

    setValueFilterSubject(value: any) {
        this.filterSearch.next(value);
    }

    getValueSubject() {
        return this.filterSearch.value;
    }

    getSearchSubject() {
        return this.filterSearch;
    }

    getFilterSubjectObservable(): Observable<any> {
        return this.filterSearch.asObservable();
    }

    listProcessFeedback(internalProcessId: number): Observable<any> {
        return this.http.get(this.apiURL() + `/api/free/internal-process/${internalProcessId}/list-user-feedbacks/`, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    submitUserFeedback(data: IInternalProcessUserFeedbackSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/free/internal-process/add-user-feedback', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    updateStatusToEvaluation(internalProcessId: number) {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/update-to-evaluation', null, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    sendToCorrection(internalProcessId: number, data: ICorrectionDataInternalProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/send-to-correction', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    sendRequesterCorrection(internalProcessId: number, data: any): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/send-requester-correction', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    saveRequesterConsiderations(internalProcessId: number, data: ISendInternalProcessConsiderationsSchema) {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/add-requester-considerations', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    requestNewAttachment(internalProcessId: number, data: any): Observable<any> {
        return this.http
            .post(
                this.apiURL() + '/api/internal-process/' + internalProcessId + '/request-new-attachment',
                data,
                this.headers(),
            )
            .pipe(catchError(this.handleError));
    }

    addResponsible(internalProcessId: number, data): Observable<any> {
        return this.http
            .post(
                this.apiURL() + '/api/internal-process/' + internalProcessId + '/add-responsible',
                data,
                this.headers(),
            )
            .pipe(catchError(this.handleError));
    }

    toggleResponsible(internalProcessId: number, data): Observable<any> {
        return this.http
            .put(
                this.apiURL() + '/api/internal-process/' + internalProcessId + '/toggle-responsible',
                data,
                this.headers(),
            )
            .pipe(catchError(this.handleError));
    }

    adminAssignResponsible(internalProcessId: number, data: { PI_CodigoResponsavel: number }): Observable<any> {
        return this.http
            .post(
                this.apiURL() + '/api/internal-process/' + internalProcessId + '/admin-assign-responsible',
                data,
                this.headers(),
            )
            .pipe(
                catchError((error) => {
                    console.error('An error occurred:', error);
                    // Repassa a mensagem da API: é ela que explica por que a atribuição foi recusada.
                    const message = error?.error?.message ?? 'Ocorreu um erro ao adicionar o responsável. Tente novamente.';
                    return throwError(() => new Error(message));
                }),
            );
    }



    transmit(internalProcessId: number, data: ITransmitInternalProcessSchema): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/transmit', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    join(internalProcessId: number, data: any): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/join', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    search(data: any): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/free/internal-process/search-internal-process', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    getTransmissions(internalProcessId: number): Observable<any> {
        return this.http.get(this.apiURL() + '/api/free/internal-process/' + internalProcessId + '/get-internal-process-transmissions', this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    newInternalProcess(internalProcessId: number, data: INewInternalProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/'+ internalProcessId + '/create-internal-process', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    markProcessAsCompleted(internalProcessId: number, data: any): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/mark-as-completed', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    markProcessAsArchived(internalProcessId: number, data: any): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/mark-as-archived', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    markProcessAsUnarchived(internalProcessId: number, data: any): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/mark-as-unarchived', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    addDispatch(internalProcessId: number, data: IAddDispachInternalProcessSchema): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/add-dispatch', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    /**
     * Anexo manual do parecer de despacho (documento elaborado fora do sistema).
     * `status` 1 anexa; `status` 0 anula o parecer informado em parecerDespacho.id.
     */
    addDispatchReport(internalProcessId: number, data: {
        parecerDespacho: { id?: number; name?: string; url?: string; justificativa?: string };
        userId: number;
        status: number;
    }): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/dispatch-report', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    // ===================================================================== //
    //  Parecer de Despacho (editor) — Processos Internos                    //
    //  Espelha os métodos de ProcessService para processos externos.        //
    // ===================================================================== //

    createParecerDraft(internalProcessId: number, data: {
        numeroParecer?: string | null;
        ementa: string;
        conteudoHtml: string;
        assinaturaSecretarioObrigatoria: boolean;
        nomeSecretario?: string | null;
        userId: number;
    }): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    updateParecerDraft(internalProcessId: number, parecerId: number, data: {
        numeroParecer?: string | null;
        ementa?: string;
        conteudoHtml?: string;
        assinaturaSecretarioObrigatoria?: boolean;
        nomeSecretario?: string | null;
    }): Observable<any> {
        return this.http.put(this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho/' + parecerId, data, this.headers())
            .pipe(catchError(this.handleError));
    }

    getParecerSecretarios(internalProcessId: number): Observable<{ secretarios: string[] }> {
        return this.http.get<{ secretarios: string[] }>(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho/secretarios',
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    previewParecer(internalProcessId: number, data: {
        numeroParecer?: string | null;
        ementa: string;
        conteudoHtml: string;
        assinaturaSecretarioObrigatoria: boolean;
        nomeSecretario?: string | null;
        userId?: number;
    }): Observable<Blob> {
        return this.http.post(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho/preview',
            data,
            { ...this.headers(), responseType: 'blob' }
        ).pipe(catchError(this.handleError));
    }

    prepareParecerSign(
        internalProcessId: number,
        parecerId: number,
    ): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    }> {
        return this.http.post<{
            pdfBase64: string;
            fileName: string;
            pageCount: number;
            signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
            secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        }>(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho/' + parecerId + '/prepare-sign',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    finalizeParecerSign(
        internalProcessId: number,
        parecerId: number,
        data: {
            fileCloudFlare: string;
            fileName: string;
            secretarioSignaturePosition?: { page: number; xPercentage: number; yPercentage: number } | null;
        },
    ): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho/' + parecerId + '/finalize-sign',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    discardParecerDraft(internalProcessId: number, parecerId: number): Observable<any> {
        return this.http.delete(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/parecer-despacho/' + parecerId,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listParecerPendingForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/internal-parecer-despacho/secretario/pending?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listParecerSignedHistoryForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/internal-parecer-despacho/secretario/history?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listParecerAllForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/internal-parecer-despacho/secretario/all?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    private buildSecretaryQueryParams(
        userId: number,
        page: number,
        perPage: number,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): string {
        const params = new URLSearchParams();
        params.set('userId', String(userId));
        params.set('page', String(page));
        params.set('perPage', String(perPage));
        if (filters?.numeroProcesso) params.set('numeroProcesso', filters.numeroProcesso);
        if (filters?.interessado) params.set('interessado', filters.interessado);
        if (filters?.ementa) params.set('ementa', filters.ementa);
        if (filters?.dataInicio) params.set('dataInicio', filters.dataInicio);
        if (filters?.dataFim) params.set('dataFim', filters.dataFim);
        return params.toString();
    }

    prepareParecerSecretarySign(parecerId: number): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    }> {
        return this.http.post<{
            pdfBase64: string;
            fileName: string;
            pageCount: number;
            signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        }>(
            this.apiURL() + '/api/internal-parecer-despacho/' + parecerId + '/prepare-secretary-sign',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    finalizeParecerSecretarySign(parecerId: number, data: { fileCloudFlare: string; fileName: string; userId: number }): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/internal-parecer-despacho/' + parecerId + '/finalize-secretary-sign',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    submitExtendsInternalProcess(data: ISubmitExtendInternalProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/extend-internal-process', data, this.headers())
    }

    confirmCancellationInternalProcess(internalProcessId: number, data: any): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/confirm-cancellation', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addRequestToCancel(internalProcessId: number, data: any): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/add-request-to-cancel', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    refuseCancellationInternalProcess(internalProcessId: number, data: any): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/' + internalProcessId + '/refuse-cancellation', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }


    getDepartamentsWithCommon(organizationIds: number[]): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/departaments-with-common',{ organizationIds }, this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }

    getSubjectsWithCommon(filters: string): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/subjects-with-common', { filters }, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    getSubjectBetweenDeps(deps: number[]): Observable<any> {
        return this.http.post(this.apiURL() + '/api/internal-process/subject-between-deps', { deps }, this.headers())
        .pipe(
            catchError((error) => {
                if (error?.status === 404) {
                    return of([]);
                }
                return this.handleError(error);
            })
        );
    }

    private handleError(error: any): Observable<never> {
        console.error('An error occurred:', error);
        return throwError(new Error(error));
    }

    getStatusDescription(status: number): string {
        const statusDescriptions = {
            1: 'Novo',
            2: 'Em Andamento',
            3: 'Arquivado',
            4: 'Concluído',
            5: 'Tramitado',
            6: 'Em Correção',
            7: 'Respondido',
            8: 'Vencido',
            9: 'Anulado'
        };

        return statusDescriptions[status];
    }

    public async generateProcessPDF(process, hideCpf = false): Promise<ISendFileProcessPdf[]> {
        const hideCpfIn = (text: string) => hideCpf ? this.maskCpf(text) : text;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
        const urlPublicProcess = environment.projectURL + '/free/processo/' + process.PRC_NumeroProcesso;
        let qrcode: string;

        QRCode.toDataURL(urlPublicProcess, (_, url) => {
            var base64Data = url.split(',')[1];
            qrcode = atob(base64Data);
        });

        const addNewPage = (title: string) => {
            doc.addPage();

            doc.setFontSize(24);
            doc.setFont('Inter-SemiBold');
            doc.setTextColor('#0A2156');
            doc.text(title, pageWidth / 2, 45, { align: 'center' });

            renderHeader();
            renderFooter();
        };

        doc.setFont('Inter-SemiBold');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text(`NPA: ${process.PI_NumeroProcessoInterno}`, pageWidth / 2, 40, {
            align: 'center',
        });

        const renderHeader = () => {
            const logoProtocolize = new Image();
            logoProtocolize.src = '../../assets/img/brand/logo-protocolizesga.png';
            doc.addImage(logoProtocolize, 'png', 60, 5, 95, 18);

            doc.addFileToVFS('Inter-Bold.ttf', interSemiBold);
            doc.addFont('Inter-Bold.ttf', 'Inter-Bold', 'normal');
            doc.setFont('Inter-SemiBold');

            doc.addFileToVFS('Inter-Regular.ttf', interRegular);
            doc.addFont('Inter-Regular.ttf', 'Inter-Regular', 'normal');

            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
            doc.text('Sistema de Protocolo e Tramitação Eletrônica', pageWidth / 2, 30, {
                align: 'center',
            });
        };

        const renderRequest = () => {
            autoTable(doc, {
                body: [
                    [
                        {
                            content: 'Solicitante:',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 'wrap',
                                fontSize: 10,
                                textColor: '#0a2156',
                                cellPadding: { left: 4, top: 2, right: 4 },
                            },
                        },
                    ],
                    [
                        {
                            content: hideCpfIn(this.formatCpfCnpj(process.pessoaCriadoraCPF)) + ' - ' + process.pessoaCriadoraNome,
                            styles: {
                                halign: 'left',
                                fontSize: 12,
                                textColor: '#687489',
                                cellPadding: {
                                    left: 4,
                                    top: 2,
                                    right: 4,
                                    bottom: 2,
                                },
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 56,
                styles: {
                    fillColor: '#f2f5f9',
                },
            });
            autoTable(doc, {
                body: [
                    [
                        {
                            content: 'Departamento atual:',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 'auto',
                                fontSize: 10,
                                textColor: '#0a2156',
                                cellPadding: { left: 4, top: 2, right: 4 },
                            },
                        },
                        {
                            content: 'Assunto:',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 'auto',
                                fontSize: 10,
                                textColor: '#0a2156',
                                cellPadding: { left: 4, top: 2, right: 4 },
                                lineWidth: { left: 1, top: 0, right: 0, bottom: 0 },
                                lineColor: '#ffffff',
                            },
                        },
                    ],
                    [
                        {
                            content: process.depDestinatarioDescricao,
                            styles: {
                                halign: 'left',
                                fontSize: 12,
                                textColor: '#687489',
                                cellPadding: {
                                    left: 4,
                                    top: 2,
                                    right: 4,
                                    bottom: 2,
                                },
                            },
                        },
                        {
                            content: process.PI_Assunto,
                            styles: {
                                halign: 'left',
                                fontSize: 12,
                                textColor: '#687489',
                                cellPadding: {
                                    left: 4,
                                    top: 2,
                                    right: 4,
                                    bottom: 2,
                                },
                                lineWidth: { left: 1, top: 0, right: 0, bottom: 0 },
                                lineColor: '#ffffff',
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 72,
                styles: {
                    fillColor: '#f2f5f9',
                },
            });
        };

        const renderFooter = () => {
            autoTable(doc, {
                body: [
                    [
                        {
                            content: (process.PI_Retroativo) ? `Situação Atual` : `Situação em ${this.datePipe.transform(new Date(), "dd/MM/yyyy 'às' HH:mm:ss")}`,
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: {
                                    bottom: 0,
                                    top: 0,
                                    right: 0,
                                    left: 3,
                                },
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: this.correctStatus(process.PIT_Status).text,
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellWidth: 85,
                                cellPadding: 4,
                                textColor: '#687489',
                            },
                        },
                        {
                            rowSpan: 3,
                            styles: {
                                halign: 'center',
                                valign: 'middle',
                                fontSize: 10,
                                textColor: '#687489',
                            },
                        },
                    ],
                    [
                        {
                            content: 'Unidade Atual',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: 4,
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: process.orgDestinatarioSigla + '-' + process.orgDestinatarioDescricao,
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellPadding: 4,
                                textColor: '#687489',
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 240,
                styles: {
                    lineWidth: 0.2,
                    lineColor: '#687489',
                    font: 'Inter-Regular',
                },
            });

            doc.addImage(qrcode, 'PNG', 171, 241, 23, 22);

            const logoSga = new Image();
            logoSga.src = '../../assets/img/brand/brasao-sga.png';
            doc.addImage(logoSga, 'png', 73, 270, 20, 18);

            const line = new Image();
            line.src = '../../assets/img/brand/line.png';
            doc.addImage(line, 'png', 100, 275, 0.2, 10);

            doc.setFont('Inter-Regular');
            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
            doc.text(this.getFooterOrganizationName(process), 107, 279, { maxWidth: 40 });
        };

        doc.setFont('Inter-SemiBold');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text('Detalhamento da solicitação:', 15, 100);

        doc.setFontSize(12);
        doc.setFont('Helvetica');
        doc.setTextColor('#687489');
        doc.text(hideCpfIn(process.PI_Observacao), 15, 110, {
            align: 'justify',
            maxWidth: 180,
        });

        const renderHistory = async () => {
            if (process.dispatch.length > 0) {
                addNewPage('Folha de Despacho');

                const dispatchData = process.dispatch.map((dispatch) => [
                    dayjs(dispatch.created_at).format('DD/MM/YYYY'),
                    hideCpfIn(dispatch.PID_Descricao),
                ]);

                const formattedData = dispatchData.map(row =>
                    row.map(cell => cell
                        .replace(/\t/g, '    ')
                    )
                );
        
                autoTable(doc, {
                    head: [['Data', 'Despacho']],
                    body: formattedData,
                    theme: 'striped',
                    startY: 65,
                    styles: {
                        font: 'Inter-Regular',
                        minCellHeight: 8,
                        valign: 'middle',
                        halign: 'left',
                    },
                    alternateRowStyles: {
                        fillColor: '#F2F5F9',
                    },
                    headStyles: {
                        fillColor: '#417bff',
                        font: 'Inter-SemiBold',
                        minCellHeight: 8,
                        valign: 'middle',
                        halign: 'left',
                    },
                });
            }

            addNewPage('Folha de Registros');

            const historicosData = process.history.map((historico) => {
                return (process.PI_Retroativo) ? [hideCpfIn(historico.PIH_Historico)] : [
                    this.datePipe.transform(historico.created_at, "dd/MM/yyyy 'às' HH:mm:ss"),
                    hideCpfIn(historico.PIH_Historico),
                ]
            });

            autoTable(doc, {
                head: [(process.PI_Retroativo) ? ['Histórico'] : ['Data', 'Histórico']],
                body: historicosData,
                theme: 'striped',
                startY: 65,
                styles: {
                    font: 'Inter-Regular',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
                alternateRowStyles: {
                    fillColor: '#F2F5F9',
                },
                headStyles: {
                    fillColor: '#417bff',
                    font: 'Inter-SemiBold',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
            });
        };
        renderHeader();
        renderRequest();
        renderFooter();
        renderHistory();

        const listBlobPdf = [
            { name: processGeneratesDocumentsName.PROCESS, file: doc.output('blob') }, 
            { name: processGeneratesDocumentsName.DOCUMENT_OF_PROCESS, file: this.generatePageDocumentsCover(process, "DOCUMENTOS DO PROCESSO", "#0A2156", qrcode)},
            { name: processGeneratesDocumentsName.ADDITIONAL_DOCUMENTS, file: this.generatePageDocumentsCover(process, "DOCUMENTOS COMPLEMENTARES", "#0A2156", qrcode, 28)},
            { name: processGeneratesDocumentsName.DISPATCH_DOCUMENTO, file: this.generatePageDocumentsCover(process, "PARECER DE DESPACHO", "#0A2156", qrcode)},
        ]

        return listBlobPdf;
    }

    public generatePageDocumentsCover(process, text: string, color: string, qrcode, fontSize = 36): Blob {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

        const logoProtocolize = new Image();
        logoProtocolize.src = '../../assets/img/brand/logo-protocolizesga.png';
        doc.addImage(logoProtocolize, 'png', 60, 5, 95, 18);

        doc.addFileToVFS('Inter-Bold.ttf', interSemiBold);
        doc.addFont('Inter-Bold.ttf', 'Inter-Bold', 'normal');
        doc.setFont('Inter-SemiBold');

        doc.addFileToVFS('Inter-Regular.ttf', interRegular);
        doc.addFont('Inter-Regular.ttf', 'Inter-Regular', 'normal');

        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text('Sistema de Protocolo e Tramitação Eletrônica', pageWidth / 2, 30, {
            align: 'center',
        });

        doc.setFontSize(fontSize);
        doc.setFont('Inter-SemiBold');
        doc.setTextColor(color);
        doc.text(text, pageWidth / 2, 140, { align: 'center' });

        autoTable(doc, {
            body: [
                [
                    {
                        content: (process.PI_Retroativo) ? `Situação Atual` : `Situação em ${this.datePipe.transform(new Date(), "dd/MM/yyyy 'às' HH:mm:ss")}`,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            cellWidth: 70,
                            fontSize: 10,
                            cellPadding: {
                                bottom: 0,
                                top: 0,
                                right: 0,
                                left: 3,
                            },
                            textColor: '#0A2156',
                        },
                    },
                    {
                        content: this.correctStatus(process.PIT_Status).text,
                        styles: {
                            halign: 'left',
                            fontSize: 10,
                            cellWidth: 85,
                            cellPadding: 4,
                            textColor: '#687489',
                        },
                    },
                    {
                        rowSpan: 3,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fontSize: 10,
                            textColor: '#687489',
                        },
                    },
                ],
                [
                    {
                        content: 'Unidade Atual',
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            cellWidth: 70,
                            fontSize: 10,
                            cellPadding: 4,
                            textColor: '#0A2156',
                        },
                    },
                    {
                        content: process.orgDestinatarioSigla + '-' + process.orgDestinatarioDescricao,
                        styles: {
                            halign: 'left',
                            fontSize: 10,
                            cellPadding: 4,
                            textColor: '#687489',
                        },
                    },
                ],
            ],
            theme: 'plain',
            startY: 240,
            styles: {
                lineWidth: 0.2,
                lineColor: '#687489',
                font: 'Inter-Regular',
            },
        });

        doc.addImage(qrcode, 'PNG', 171, 241, 23, 22);

        const logoSga = new Image();
        logoSga.src = '../../assets/img/brand/brasao-sga.png';
        doc.addImage(logoSga, 'png', 73, 270, 20, 18);

        const line = new Image();
        line.src = '../../assets/img/brand/line.png';
        doc.addImage(line, 'png', 100, 275, 0.2, 10);

        doc.setFont('Inter-Regular');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text(this.getFooterOrganizationName(process), 107, 279, { maxWidth: 40 });

        return doc.output('blob');
    }

    public async generateAdminProcessPDF(process): Promise<ISendFileProcessPdf[]> {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
        const urlPublicProcess = environment.projectURL + '/free/processo/' + process.PRC_NumeroProcesso;
        let qrcode: string;
    
        QRCode.toDataURL(urlPublicProcess, (_, url) => {
            var base64Data = url.split(',')[1];
            qrcode = atob(base64Data);
        });
    
        const addNewPage = (title: string) => {
            doc.addPage();
    
            doc.setFontSize(24);
            doc.setFont('Inter-SemiBold');
            doc.setTextColor('#0A2156');
            doc.text(title, pageWidth / 2, 45, { align: 'center' });
    
            renderHeader();
            renderFooter();
        };
    
        doc.setFont('Inter-SemiBold');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text(`NPAD: ${process.PI_NumeroProcessoInterno}`, pageWidth / 2, 40, {
            align: 'center',
        });
    
        const renderHeader = () => {
            const logoProtocolize = new Image();
            logoProtocolize.src = '../../assets/img/brand/logo-protocolizesga.png';
            doc.addImage(logoProtocolize, 'png', 60, 5, 95, 18);
    
            doc.addFileToVFS('Inter-Bold.ttf', interSemiBold);
            doc.addFont('Inter-Bold.ttf', 'Inter-Bold', 'normal');
            doc.setFont('Inter-SemiBold');
    
            doc.addFileToVFS('Inter-Regular.ttf', interRegular);
            doc.addFont('Inter-Regular.ttf', 'Inter-Regular', 'normal');
    
            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
            doc.text('Sistema de Protocolo e Tramitação Eletrônica', pageWidth / 2, 30, {
                align: 'center',
            });
        };
    
        const renderRequest = () => {
            autoTable(doc, {
                body: [
                    [
                        {
                            content: 'Interessado:',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 'wrap',
                                fontSize: 10,
                                textColor: '#0a2156',
                                cellPadding: { left: 4, top: 2, right: 4 },
                            },
                        },
                    ],
                    [
                        {
                            content: this.formatCpfCnpj(process.PAD_CPF) + ' - ' + (process.PAD_Nome),
                            styles: {
                                halign: 'left',
                                fontSize: 12,
                                textColor: '#687489',
                                cellPadding: {
                                    left: 4,
                                    top: 2,
                                    right: 4,
                                    bottom: 2,
                                },
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 56,
                styles: {
                    fillColor: '#f2f5f9',
                },
            });
            
            autoTable(doc, {
                body: [
                    [
                        {
                            content: 'Órgão/Destinatário:',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 'auto',
                                fontSize: 10,
                                textColor: '#0a2156',
                                cellPadding: { left: 4, top: 2, right: 4 },
                            },
                        },
                    ],
                    [
                        {
                            content: process.orgDestinatarioSigla + ' - ' + process.orgDestinatarioDescricao,
                            styles: {
                                halign: 'left',
                                fontSize: 12,
                                textColor: '#687489',
                                cellPadding: {
                                    left: 4,
                                    top: 2,
                                    right: 4,
                                    bottom: 2,
                                },
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 72,
                styles: {
                    fillColor: '#f2f5f9',
                },
            });
        };
    
        const renderFooter = () => {
            autoTable(doc, {
                body: [
                    [
                        {
                            content: (process.PI_Retroativo) ? `Situação Atual` : `Situação em ${this.datePipe.transform(new Date(), "dd/MM/yyyy 'às' HH:mm:ss")}`,
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: {
                                    bottom: 0,
                                    top: 0,
                                    right: 0,
                                    left: 3,
                                },
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: this.correctStatus(process.PIT_Status).text,
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellWidth: 85,
                                cellPadding: 4,
                                textColor: '#687489',
                            },
                        },
                        {
                            rowSpan: 3,
                            styles: {
                                halign: 'center',
                                valign: 'middle',
                                fontSize: 10,
                                textColor: '#687489',
                            },
                        },
                    ],
                    [
                        {
                            content: 'Unidade Atual',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: 4,
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: process.orgDestinatarioSigla + '-' + process.orgDestinatarioDescricao,
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellPadding: 4,
                                textColor: '#687489',
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 240,
                styles: {
                    lineWidth: 0.2,
                    lineColor: '#687489',
                    font: 'Inter-Regular',
                },
            });
    
            doc.addImage(qrcode, 'PNG', 171, 241, 23, 22);
    
            const logoSga = new Image();
            logoSga.src = '../../assets/img/brand/brasao-sga.png';
            doc.addImage(logoSga, 'png', 73, 270, 20, 18);
    
            const line = new Image();
            line.src = '../../assets/img/brand/line.png';
            doc.addImage(line, 'png', 100, 275, 0.2, 10);
    
            doc.setFont('Inter-Regular');
            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
            doc.text(this.getFooterOrganizationName(process), 107, 279, { maxWidth: 40 });
            
            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor('#687489');
            }
        };
    
        doc.setFont('Inter-SemiBold');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text('Detalhamento da solicitação:', 15, 100);
    
        doc.setFontSize(12);
        doc.setFont('Helvetica');
        doc.setTextColor('#687489');
        doc.text(process.PI_Observacao, 15, 110, {
            align: 'justify',
            maxWidth: 180,
        });
    
        const renderHistory = async () => {
            if (process.dispatch.length > 0) {
                addNewPage('Folha de Despacho');
    
                const dispatchData = process.dispatch.map((dispatch) => [
                    dayjs(dispatch.created_at).format('DD/MM/YYYY'),
                    dispatch.PID_Descricao,
                ]);
    
                const formattedData = dispatchData.map(row =>
                    row.map(cell => cell
                        .replace(/\t/g, '    ')
                    )
                );
        
                autoTable(doc, {
                    head: [['Data', 'Despacho']],
                    body: formattedData,
                    theme: 'striped',
                    startY: 65,
                    styles: {
                        font: 'Inter-Regular',
                        minCellHeight: 8,
                        valign: 'middle',
                        halign: 'left',
                    },
                    alternateRowStyles: {
                        fillColor: '#F2F5F9',
                    },
                    headStyles: {
                        fillColor: '#417bff',
                        font: 'Inter-SemiBold',
                        minCellHeight: 8,
                        valign: 'middle',
                        halign: 'left',
                    },
                });
            }
    
            addNewPage('Folha de Registros');
    
            const historicosData = process.history.map((historico) => {
                const historicoText = (process.PIT_Status === internalProcessStatusEnum.INSTAURADO) 
                                    ? 'Aguardando resposta do cidadão' 
                                    : historico.PIH_Historico;
    
                return (process.PI_Retroativo) ? [historicoText] : [
                    this.datePipe.transform(historico.created_at, "dd/MM/yyyy 'às' HH:mm:ss"),
                    historicoText,
                ]
            });
    
            autoTable(doc, {
                head: [(process.PI_Retroativo) ? ['Histórico'] : ['Data', 'Histórico']],
                body: historicosData,
                theme: 'striped',
                startY: 65,
                styles: {
                    font: 'Inter-Regular',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
                alternateRowStyles: {
                    fillColor: '#F2F5F9',
                },
                headStyles: {
                    fillColor: '#417bff',
                    font: 'Inter-SemiBold',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
            });
        };
    
        renderHeader();
        renderRequest();
        renderFooter();
        await renderHistory();
    
        const listBlobPdf = [
            { name: processGeneratesDocumentsName.PROCESS, file: doc.output('blob') }, 
            { name: processGeneratesDocumentsName.DOCUMENT_OF_PROCESS, file: this.generatePageDocumentsCover(process, "DOCUMENTOS DO PROCESSO", "#0A2156", qrcode)},
            { name: processGeneratesDocumentsName.ADDITIONAL_DOCUMENTS, file: this.generatePageDocumentsCover(process, "DOCUMENTOS COMPLEMENTARES", "#0A2156", qrcode, 28)},
            { name: processGeneratesDocumentsName.DISPATCH_DOCUMENTO, file: this.generatePageDocumentsCover(process, "PARECER DE DESPACHO", "#0A2156", qrcode)},
        ]
    
        return listBlobPdf;
    }

    private getFooterOrganizationName(process): string {
        // Processos concluídos, arquivados e anulados são tramitados sem departamento
        // tramitador, então o órgão do destinatário é usado como referência.
        return process.orgTramitadorDescricao || process.orgDestinatarioDescricao || '';
    }

    public formatCpfCnpj(cpfCnpj: string) {
        const isCPF = cpfCnpj.length === 11;

        return cpfCnpj.replace(
            isCPF ? /^(\d{3})(\d{3})(\d{3})(\d{2})$/ : /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
            isCPF ? '$1.$2.$3-$4' : '$1.$2.$3/$4-$5',
        );
    }

    /** `badge`: classes do ui-badge (request-ui.scss), o badge "with dot · modern" do Untitled. */
    public correctStatus(status: number): { text: string, class: string, color: string, badge: string } {
        switch (status) {
            case internalProcessStatusEnum.NOVO:
                return {
                    text: internalProcessStatusText.NOVO,
                    badge: "ui-badge ui-badge--sm ui-badge--brand",
                    class: "badge-primary",
                    color: "bg-primary text-white"
                }
            case internalProcessStatusEnum.EM_ANDAMENTO:
                return {
                    text: internalProcessStatusText.EM_ANDAMENTO,
                    badge: "ui-badge ui-badge--sm ui-badge--blue-light",
                    class: "badge-info",
                    color: "bg-info text-white"
                }
            case internalProcessStatusEnum.ARQUIVADO:
                return {
                    text: internalProcessStatusText.ARQUIVADO,
                    badge: "ui-badge ui-badge--sm ui-badge--gray",
                    class: "badge-secondary text-muted",
                    color: "badge-secondary bg-dark text-white"
                };
            case internalProcessStatusEnum.CONCLUIDO:
                return {
                    text: internalProcessStatusText.CONCLUIDO,
                    badge: "ui-badge ui-badge--sm ui-badge--success",
                    class: "badge-success",
                    color: "bg-success text-white"
                };
            case internalProcessStatusEnum.CORRECAO:
                return {
                    text: internalProcessStatusText.CORRECAO,
                    badge: "ui-badge ui-badge--sm ui-badge--warning",
                    class: "",
                    color: "bg-warning text-white "
                };
            case internalProcessStatusEnum.RESPONDIDO:
                return {
                    text: internalProcessStatusText.RESPONDIDO,
                    badge: "ui-badge ui-badge--sm ui-badge--indigo",
                    class: "",
                    color: "bg-success-light"
                };
            case internalProcessStatusEnum.VENCIDO:
                return {
                    text: internalProcessStatusText.VENCIDO,
                    badge: "ui-badge ui-badge--sm ui-badge--orange",
                    class: "",
                    color: "bg-red text-white"
                };
            case internalProcessStatusEnum.ANULADO:
                return {
                    text: internalProcessStatusText.ANULADO,
                    badge: "ui-badge ui-badge--sm ui-badge--error",
                    class: "badge-danger",
                    color: "bg-red text-white"
                };
            case internalProcessStatusEnum.EM_TRIAGEM:
                return {
                    text: internalProcessStatusText.EM_TRIAGEM,
                    badge: "ui-badge ui-badge--sm ui-badge--purple",
                    class: "badge-evaluation",
                    color: "bg-evaluation  text-primary"
                }    
            case internalProcessStatusEnum.INSTAURADO:
                return {
                    text: internalProcessStatusText.INSTAURADO,
                    badge: "ui-badge ui-badge--sm ui-badge--blue",
                    class: "",
                    color: "bg-red text-white"
                }    
            case internalProcessStatusEnum.INTIMADO:
                return {
                    text: internalProcessStatusText.INTIMADO,
                    badge: "ui-badge ui-badge--sm ui-badge--pink",
                    class: "",
                    color: "bg-processed text-white"
                }    
            case internalProcessStatusEnum.EM_ANALISE_DEFESA:
                return {
                    text: internalProcessStatusText.EM_ANALISE_DEFESA,
                    badge: "ui-badge ui-badge--sm ui-badge--indigo",
                    class: "",
                    color: "bg-info  text-white"
                }    
            case internalProcessStatusEnum.PENDENTE:
                return {
                    text: internalProcessStatusText.PENDENTE,
                    badge: "ui-badge ui-badge--sm ui-badge--gray-blue",
                    class: "",
                    color: "bg-warning text-white"
                }
            case internalProcessStatusEnum.ARQUIVADO_SEM_ANALISE:
                return {
                    text: internalProcessStatusText.ARQUIVADO_SEM_ANALISE,
                    badge: "ui-badge ui-badge--sm ui-badge--yellow",
                    class: "badge-secondary text-muted",
                    color: "badge-secondary bg-dark text-white"
                };
        }
    }

    public reopenInternalProcess(internalProcessId: number) {
        return this.http.get(this.apiURL() + '/api/internal-process/' + internalProcessId + '/reopen-internal-process', this.headers())
        .pipe(catchError(this.handleError));
    }

    public getStatusToRestore(internalProcessId: number): Observable<{ status: number }> {
        return this.http.get<{ status: number }>(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/status-to-restore',
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    public deleteHistory(historyId: number) {
        return this.http.delete(this.apiURL() + '/api/internal-process/history/' + historyId, this.headers())
        .pipe(catchError(this.handleError));
    }

    public deleteDispatch(dispatchId: number) {
        return this.http.delete(this.apiURL() + '/api/internal-process/dispatch/' + dispatchId, this.headers())
        .pipe(catchError(this.handleError));
    }

    public deleteConsideration(considerationId: number) {
        return this.http.delete(this.apiURL() + '/api/internal-process/consideration/' + considerationId, this.headers())
        .pipe(catchError(this.handleError));
    }

    public deleteProcessingNote(noteId: number) {
        return this.http.delete(this.apiURL() + '/api/internal-process/processing-note/' + noteId, this.headers())
        .pipe(catchError(this.handleError));
    }

    public mergedDocumentInPdf(files: ISendFileProcessPdf[], internalProcessId: number, internalProcessNumber: string) {
        const formData = new FormData()
        for (const fileElement of files) {
            formData.append(fileElement.name + ".pdf", fileElement.file)
        }   
        return this.http.post(this.apiURL() + '/api/pdf/internal-process/' + internalProcessId + '/' + internalProcessNumber , formData , { ...this.headers(true), responseType: 'blob', reportProgress: true, observe: 'events'}); 
    }

    countProcesses(data: any): Observable<any> {
        return this.http.get<any>(`${this.apiURL()}/api/internal-process/list-count`,
          {
            params: data,
            ...this.headers()
          }
        );
    }

    getTotalProcessingNotes(internalProcessId: number) {
        return this.http.get(this.apiURL() + '/api/internal-process/' + internalProcessId + '/get-total-processing-notes', this.headers());
    }

    getConsiderations(internalProcessId: number) {
        return this.http.get(this.apiURL() + '/api/internal-process/' + internalProcessId + '/get-considerations', this.headers());
    }

    public generatePdfDispatch(processoId: number, body: {html: string}): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/parecer-despacho-pdf/' + processoId,
            body, 
            this.headers()
        );
    }

    public submitDefense(internalProcessId: number, defenseData: any): Observable<any> {
        return this.http.post(
            `${this.apiURL()}/api/internal-process/${internalProcessId}/submit-defense`, 
            defenseData, 
            this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }

    updatePadStatus(processId: number, status: internalProcessStatusEnum): Observable<any> {
        return this.http.post(
            `${this.apiURL()}/api/internal-process/${processId}/update-pad-status`, 
            { status }, 
            this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }

    public sendPadInformation(internalProcessId: number, padData: any): Observable<any> {
        return this.http.post(
            `${this.apiURL()}/api/internal-process/${internalProcessId}/send-pad-information`,
            padData,
            this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }

    shareView(internalProcessId: number, data: any): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/internal-process/' + internalProcessId + '/share-view',
            data,
            this.headers(),
        )
        .pipe(
            catchError(this.handleError)
        );
    }

    public savePermission(
        internalProcessId: number,
        data: any
    ): Observable<any> {
        return this.http.post(
            `${this.apiURL()}/api/internal-process/${internalProcessId}/save-permission`,
            data,
            this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }

    public listPermissionProcesses(): Observable<any> {
        return this.http
            .get(
                `${this.apiURL()}/api/internal-process/list-permissions`,
                this.headers()
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public getPermissionHistory(internalProcessId: number): Observable<any> {
        return this.http
            .get(
                `${this.apiURL()}/api/internal-process/${internalProcessId}/permission-history`,
                this.headers()
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    public removePermission(permissionId: number): Observable<any> {
        return this.http.delete(
            `${this.apiURL()}/api/internal-process/permissao/${permissionId}`,
            this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }
}
